// Migrated 1:1 from @nexato/nx-core-module (file/components/file-gallery, #686)
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, OnDestroy, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { v4 as uuidv4 } from 'uuid';
import { finalize, tap } from 'rxjs/operators';
import { RolesService } from '../../services/roles/roles.service';
import { FileTypes } from '../../entities/file/file';
import { FilesService } from '../../services/file/file.service';
import { File } from '../../entities/file/file';
import { ConfirmDialogService } from '../confirmDialog/confirmDialogService';
import { ConfirmDialogEventType, ConfirmDialogSeverity } from '../confirmDialog/confirmDialogComponent';
import { RefetchCoordinator } from '../../services/refetch-coordinator/refetch-coordinator.service';
import { Reloadable } from '../../services/command/command.service';

@Component({
  selector: 'app-file-gallery',
  templateUrl: './file-gallery.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FileGalleryComponent implements OnInit, OnDestroy, Reloadable {

  private readonly destroyRef = inject(DestroyRef);
  readonly rolesService = inject(RolesService);
  private readonly filesService = inject(FilesService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly refetchCoordinator = inject(RefetchCoordinator);

  private unregisterRefetch?: () => void;

  readonly headline = input<string>();
  readonly ownerId = input<string>();
  readonly ownerType = input<string>();
  readonly role = input<string>();
  readonly emptyMessage = input<string>();
  readonly maxFileSize = input<number>(10 * 1024 * 1024); // 10MB default

  readonly files = signal<File[]>([]);
  readonly uploading = signal(false);
  readonly uploadProgress = signal<{ current: number; total: number } | null>(null);
  readonly isDragging = signal(false);
  readonly dropError = signal<string | null>(null);

  // Permission computed signals
  readonly canUpload = computed(() =>
    this.rolesService.hasRole('framework_files_manage')
  );

  readonly canDelete = computed(() =>
    this.rolesService.hasRole('framework_files_manage')
  );

  // Message dictionary for i18n readiness
  private readonly messages = {
    deleteConfirmTitle: 'Bild löschen',
    deleteConfirmMessage: 'Möchtest Du dieses Bild wirklich löschen?',
    deleteConfirmCancel: 'Abbrechen',
    deleteConfirmConfirm: 'Löschen',
    dropHere: 'Bilder hier ablegen',
    upload: 'Bilder hochladen',
    uploaded: 'hochgeladen',
    fileSizeExceeded: 'Bild(er) überschreiten {size} Limit',
    invalidFormat: 'Nur JPG, JPEG, PNG Bilder erlaubt',
    rejected: 'Bilder abgelehnt (ungültiges Format oder zu groß)',
    skipped: '{count} Bild(er) übersprungen',
    skippedFormat: '(ungültiges Format)',
    skippedSize: '(überschreitet {size})',
    skippedBoth: '(ungültiges Format oder zu groß)'
  } as const;

  // Expose messages for template
  readonly dropHereText = this.messages.dropHere;
  readonly uploadedText = this.messages.uploaded;
  readonly uploadText = this.messages.upload;

  private readonly acceptedFileTypes: readonly FileTypes[] = [
    FileTypes.JPG,
    FileTypes.JPEG,
    FileTypes.PNG
  ] as const;

  readonly acceptString = computed(() => this.acceptedFileTypes.join(', '));

  constructor() {
    effect(() => {
      const id = this.ownerId();
      if (id) {
        this.loadFiles();
      }
    });
  }

  ngOnInit(): void {
    const ownerId = this.ownerId();
    const ownerType = this.ownerType();
    if (ownerId && ownerType) {
      this.unregisterRefetch = this.refetchCoordinator.register(
        this,
        ownerType,
        ownerId
      );
    }
  }

  ngOnDestroy(): void {
    this.unregisterRefetch?.();
  }

  reload(): void {
    this.loadFiles();
  }

  private loadFiles(): void {
    this.filesService.getFiles({
      ownerId: this.ownerId(),
      role: this.role()
    }).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(result => {
      this.files.set(result);
    });
  }

  fileInputChangedEvent(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!this.canUpload() || this.uploading() || !input.files?.length || !this.ownerId() || !this.role()) {
      return;
    }

    const allFiles = Array.from(input.files);
    input.value = '';  // Reset input to allow re-uploading the same file

    const { valid: validFiles, invalidType, invalidSize } = this.filterValidFiles(allFiles);

    if (validFiles.length === 0) {
      this.showValidationError(invalidType, invalidSize);
      return;
    }

    if (validFiles.length < allFiles.length) {
      this.showSkippedFilesWarning(invalidType, invalidSize);
    }

    this.uploadFiles(validFiles);
  }

  deleteFile(file: File): void {
    if (!this.canDelete()) {
      return;
    }

    const dialogRef = this.confirmDialogService.open({
      header: this.messages.deleteConfirmTitle,
      message: this.messages.deleteConfirmMessage,
      cancelLabel: this.messages.deleteConfirmCancel,
      severity: ConfirmDialogSeverity.WARN,
      confirmLabel: this.messages.deleteConfirmConfirm
    });

    dialogRef.onClose.subscribe((result: ConfirmDialogEventType) => {
      if (result === ConfirmDialogEventType.CONFIRM) {
        this.filesService.deleteFile(file, () => {
          this.loadFiles();
          this.emitRefetchEvent();
        });
      }
    });
  }

  // Drag & Drop handlers
  onDragOver(event: DragEvent): void {
    if (!this.canUpload()) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (!this.uploading()) {
      this.isDragging.set(true);
    }
  }

  onDragLeave(event: DragEvent): void {
    if (!this.canUpload()) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
  }

  onDrop(event: DragEvent): void {
    if (!this.canUpload()) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);

    if (this.uploading() || !this.ownerId() || !this.role()) {
      return;
    }

    const files = event.dataTransfer?.files;
    if (files?.length) {
      const allFiles = Array.from(files);
      const { valid: validFiles, invalidType, invalidSize } = this.filterValidFiles(allFiles);

      if (validFiles.length === 0) {
        this.showValidationError(invalidType, invalidSize);
      } else if (validFiles.length < allFiles.length) {
        this.showSkippedFilesWarning(invalidType, invalidSize);
        this.uploadFiles(validFiles);
      } else {
        this.uploadFiles(validFiles);
      }
    }
  }

  private showValidationError(invalidType: number, invalidSize: number): void {
    if (invalidSize > 0 && invalidType === 0) {
      this.showDropError(this.messages.fileSizeExceeded.replace('{size}', this.formatFileSize(this.maxFileSize())));
    } else if (invalidType > 0 && invalidSize === 0) {
      this.showDropError(this.messages.invalidFormat);
    } else {
      this.showDropError(this.messages.rejected);
    }
  }

  private showSkippedFilesWarning(invalidType: number, invalidSize: number): void {
    const skipped = invalidType + invalidSize;
    let message = this.messages.skipped.replace('{count}', String(skipped));
    if (invalidType > 0 && invalidSize > 0) {
      message += ' ' + this.messages.skippedBoth;
    } else if (invalidSize > 0) {
      message += ' ' + this.messages.skippedSize.replace('{size}', this.formatFileSize(this.maxFileSize()));
    } else {
      message += ' ' + this.messages.skippedFormat;
    }
    this.showDropError(message);
  }

  private showDropError(message: string): void {
    this.dropError.set(message);
    setTimeout(() => this.dropError.set(null), 3000);
  }

  private filterValidFiles(files: globalThis.File[]): { valid: globalThis.File[]; invalidType: number; invalidSize: number } {
    // acceptedFileTypes contains MIME types like 'image/jpeg'
    const acceptedMimeTypes = this.acceptedFileTypes.map(t => t.toLowerCase());
    const maxSize = this.maxFileSize();

    let invalidType = 0;
    let invalidSize = 0;

    const valid = files.filter(file => {
      const mimeType = file.type.toLowerCase();
      if (!acceptedMimeTypes.includes(mimeType)) {
        invalidType++;
        return false;
      }
      if (maxSize && file.size > maxSize) {
        invalidSize++;
        return false;
      }
      return true;
    });

    return { valid, invalidType, invalidSize };
  }

  private formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  private uploadFiles(files: globalThis.File[]): void {
    if (!files.length) return;

    const batchId = uuidv4();

    this.uploading.set(true);
    this.uploadProgress.set({ current: 0, total: files.length });

    let uploadedCount = 0;

    this.filesService.uploadFilesV2Batch(
      files,
      this.ownerId()!,
      this.role()!,
      batchId,
      this.ownerType()
    ).pipe(
      tap(() => {
        uploadedCount++;
        this.uploadProgress.set({ current: uploadedCount, total: files.length });
      }),
      finalize(() => this.returnUpload()),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  private returnUpload(): void {
    setTimeout(() => {
      this.uploading.set(false);
      this.uploadProgress.set(null);
      this.loadFiles();
      this.emitRefetchEvent();
    }, 100);
  }

  private emitRefetchEvent(): void {
    const ownerId = this.ownerId();
    const ownerType = this.ownerType();
    if (ownerId && ownerType) {
      this.refetchCoordinator.emit({
        entityType: ownerType,
        entityId: ownerId
      });
    }
  }
}
